<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div class="main-container">
        <div class="articulo-principal properties-view">
          <header class="properties-header">
            <div class="header-content">
              <div class="breadcrumbs">
                <span>Administración</span>
                <span class="separator">/</span>
                <span class="current">Propiedades</span>
              </div>
              <h1>Propiedades</h1>
              <p class="lede">Gestiona las propiedades bajo administración.</p>
            </div>
            <button class="btn-primary" type="button" @click="startNewProperty">
              <b-icon icon="plus-circle" aria-hidden="true" />
              Agregar propiedad
            </button>
          </header>

          <div class="properties-content">
            <div class="filters-bar">
              <div class="search-input">
                <b-icon icon="search" class="search-icon" aria-hidden="true" />
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Buscar por dirección, comuna o tipo..."
                />
              </div>
            </div>

            <div class="tabs-nav">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                class="tab-btn"
                :class="{ 'active': currentTab === tab.id }"
                @click="currentTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </div>

            <div v-if="loading" class="loading-state">
              <div class="spinner"></div>
              <p>Cargando propiedades...</p>
            </div>

            <div v-else class="properties-grid">
              <div
                v-for="prop in filteredProperties"
                :key="prop.id"
                class="property-card"
              >
                <div class="card-image-container">
                  <img :src="getPropertyImage(prop)" alt="Property Image" class="card-image" />
                  <span class="property-tag">{{ prop.tipo_propiedad || 'Propiedad' }}</span>

                  <div class="card-quick-actions">
                    <button class="action-btn edit-btn" title="Editar" @click.stop="editProperty(prop)">
                      <b-icon icon="pencil" aria-hidden="true" />
                    </button>
                    <button class="action-btn delete-btn" title="Eliminar" @click.stop="confirmRemove(prop)">
                      <b-icon icon="trash" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div class="card-body">
                  <h3 class="card-title">{{ prop.nombre || prop.direccion }}</h3>
                  <p class="card-subtitle">{{ prop.direccion }}</p>
                  <p v-if="getComunaName(prop)" class="card-comuna">{{ getComunaName(prop) }}</p>

                  <div class="requests-chip" :class="{ 'has-requests': getRequestsCount(prop) > 0 }">
                    <span class="requests-count">{{ getRequestsCount(prop) }}</span>
                    <span class="requests-text">solicitudes activas</span>
                  </div>

                  <button class="btn-manage" @click="manageProperty(prop)">
                    Gestionar Propiedad
                    <b-icon icon="arrow-right" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <button class="new-unit-card" @click="startNewProperty">
                <div class="new-unit-content">
                  <div class="icon-circle">
                    <b-icon icon="house" aria-hidden="true" />
                    <span class="plus-badge">+</span>
                  </div>
                  <h3>¿Nueva unidad?</h3>
                  <p>Añade una propiedad para su administración</p>
                </div>
              </button>
            </div>
          </div>

          <div v-if="showModal" class="modal-backdrop" @click.self="cancelEdit">
            <div class="modal-container">
              <div class="modal-header">
                <h2 class="form-title">{{ isCreating ? 'Nueva propiedad' : 'Editar propiedad' }}</h2>
                <button class="close-modal-btn" @click="cancelEdit">
                  <b-icon icon="x" aria-hidden="true" />
                </button>
              </div>

              <div class="modal-body">
                <div class="form-grid">
                  <label class="field">
                    <span class="field__label">Nombre o referencia</span>
                    <input v-model="form.nombre" type="text" placeholder="Ej: Torre Norte" />
                  </label>

                  <label class="field">
                    <span class="field__label">Dirección</span>
                    <input v-model="form.direccion" type="text" placeholder="Ingresa la dirección completa" />
                  </label>

                  <label class="field">
                    <span class="field__label">Comuna</span>
                    <select v-model="form.comuna_id">
                      <option :value="null">Selecciona una comuna</option>
                      <option v-for="c in comunas" :key="c.id" :value="c.id">
                        {{ c.Nombre }}
                      </option>
                    </select>
                  </label>

                  <div class="field-row">
                    <label class="field">
                      <span class="field__label">Tipo de propiedad</span>
                      <select v-model="form.tipo_propiedad">
                        <option value="">Seleccionar</option>
                        <option value="Departamento">Departamento</option>
                        <option value="Casa">Casa</option>
                        <option value="Oficina">Oficina</option>
                        <option value="Local">Local</option>
                        <option value="Bodega">Bodega</option>
                      </select>
                    </label>
                    <label class="field">
                      <span class="field__label">Número/departamento</span>
                      <input v-model="form.numero_apartamento" type="text" placeholder="Ej: 1204" />
                    </label>
                  </div>

                  <div class="form-divider">
                    <span>Contacto del arrendatario (opcional)</span>
                  </div>

                  <div class="field-row">
                    <label class="field">
                      <span class="field__label">Nombre (opcional)</span>
                      <input v-model="form.contacto_nombre" type="text" placeholder="Nombre del contacto" />
                    </label>
                    <label class="field">
                      <span class="field__label">Teléfono (opcional)</span>
                      <input v-model="form.contacto_telefono" type="text" placeholder="Teléfono del contacto" />
                    </label>
                  </div>

                  <label class="field">
                    <span class="field__label">Email (opcional)</span>
                    <input v-model="form.contacto_email" type="email" placeholder="correo@ejemplo.com" />
                  </label>

                  <p v-if="error" class="field__error">
                    <b-icon icon="exclamation-circle" aria-hidden="true" />
                    {{ error }}
                  </p>
                </div>
              </div>

              <div class="modal-footer">
                <div class="modal-footer-left">
                  <button v-if="!isCreating" class="btn-danger-text" type="button" @click="remove">
                    Eliminar propiedad
                  </button>
                </div>
                <div class="modal-footer-right">
                  <button class="btn-secondary" type="button" @click="cancelEdit">
                    Cancelar
                  </button>
                  <button class="btn-primary" type="button" :disabled="saving" @click="save">
                    {{ saving ? 'Guardando...' : 'Guardar propiedad' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import TopNav from '@/components/TopNav.vue'
import SideNav from '@/components/SideNav.vue'

export default {
  name: 'PropiedadesAdministrador',
  components: {
    TopNav,
    SideNav
  },
  data() {
    return {
      propiedades: [],
      loading: true,
      searchQuery: '',
      currentTab: 'all',
      tabs: [
        { id: 'all', label: 'Todas' },
        { id: 'Departamento', label: 'Departamentos' },
        { id: 'Casa', label: 'Casas' },
        { id: 'Oficina', label: 'Oficinas' }
      ],
      showModal: false,
      editingId: null,
      isCreating: false,
      saving: false,
      error: '',
      form: {
        nombre: '',
        direccion: '',
        comuna_id: null,
        tipo_propiedad: '',
        numero_apartamento: '',
        contacto_nombre: '',
        contacto_email: '',
        contacto_telefono: ''
      }
    }
  },
  computed: {
    ...mapGetters(['comunas']),
    filteredProperties() {
      let filtered = this.propiedades

      if (this.currentTab !== 'all') {
        filtered = filtered.filter((p) =>
          (p.tipo_propiedad || '').toLowerCase() === this.currentTab.toLowerCase()
        )
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        filtered = filtered.filter((p) =>
          (p.nombre || '').toLowerCase().includes(q) ||
          (p.direccion || '').toLowerCase().includes(q) ||
          (p.tipo_propiedad || '').toLowerCase().includes(q) ||
          this.getComunaName(p).toLowerCase().includes(q)
        )
      }

      return filtered
    }
  },
  watch: {
    showModal(val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },
  mounted() {
    this.fetchProperties()
  },
  methods: {
    fetchProperties() {
      this.loading = true
      axios.get(`${window.hostname}api/property_manager/propiedades`)
        .then((res) => {
          this.propiedades = res.data?.propiedades || []
        })
        .catch(() => {
          this.propiedades = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    getPropertyImage(prop) {
      const mainImage = prop?.foto_principal?.link
      if (mainImage) {
        return mainImage
      }
      const type = (prop.tipo_propiedad || '').toLowerCase()
      if (type.includes('casa')) return 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
      if (type.includes('oficina')) return 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
      return 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
    },
    getRequestsCount(prop) {
      return Number(prop.solicitudes_activas_count || 0)
    },
    getComunaName(prop) {
      if (!prop) {
        return ''
      }
      if (prop.comuna_nombre) {
        return prop.comuna_nombre
      }
      if (!prop.comuna_id || !Array.isArray(this.comunas)) {
        return ''
      }
      const comuna = this.comunas.find(item => item.id === prop.comuna_id)
      return comuna?.Nombre || ''
    },
    manageProperty(prop) {
      this.$router.push(`/administrador-propiedades/Propiedades/${prop.id}`)
    },
    startNewProperty() {
      this.isCreating = true
      this.editingId = null
      this.resetForm()
      this.showModal = true
    },
    editProperty(prop) {
      this.isCreating = false
      this.editingId = prop.id
      this.form = { ...prop }
      this.showModal = true
    },
    confirmRemove(prop) {
      this.editingId = prop.id
      this.remove()
    },
    cancelEdit() {
      this.showModal = false
      setTimeout(() => {
        this.isCreating = false
        this.editingId = null
        this.resetForm()
        this.error = ''
      }, 300)
    },
    resetForm() {
      this.form = {
        nombre: '',
        direccion: '',
        comuna_id: null,
        tipo_propiedad: '',
        numero_apartamento: '',
        contacto_nombre: '',
        contacto_email: '',
        contacto_telefono: ''
      }
    },
    save() {
      if (!this.form.direccion.trim()) {
        this.error = 'La dirección es obligatoria.'
        return
      }
      this.error = ''
      this.saving = true

      const payload = { ...this.form }
      const url = this.editingId
        ? `${window.hostname}api/property_manager/propiedades/${this.editingId}`
        : `${window.hostname}api/property_manager/propiedades`

      const method = this.editingId ? axios.put : axios.post

      method(url, payload)
        .then(() => {
          this.fetchProperties()
          this.cancelEdit()
        })
        .catch((err) => {
          const message = err.response?.data?.message || 'Error al guardar.'
          this.error = message
        })
        .finally(() => {
          this.saving = false
        })
    },
    remove() {
      if (!confirm('¿Seguro que quieres eliminar esta propiedad?')) return

      axios.delete(`${window.hostname}api/property_manager/propiedades/${this.editingId}`)
        .then(() => {
          this.fetchProperties()
          this.cancelEdit()
        })
        .catch(() => {
          this.error = 'No se pudo eliminar.'
        })
    }
  }
}
</script>

<style scoped>
.properties-view {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.column-right {
  background-color: rgb(242, 247, 251);
  padding-top: 80px;
  padding-left: 260px;
  width: 100%;
  min-height: 133vh;
  min-height: 133dvh;
  box-sizing: border-box;
}

.main-container {
  background-color: white;
  margin: 20px;
  padding: 20px 20px 10px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.properties-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 20px;
}

.header-content {
  flex: 1;
  min-width: 200px;
}

.breadcrumbs {
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
}

.separator {
  margin: 0 8px;
  color: #cbd5e1;
}

.current {
  color: #2563eb;
  font-weight: 600;
}

.properties-header h1 {
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
}

.lede {
  color: #64748b;
  margin: 0;
  font-size: 1.05rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #2563eb;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
}

.btn-primary :deep(svg) {
  width: 18px;
  height: 18px;
}

.btn-primary:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5f5;
  color: #0f172a;
}

.btn-danger-text {
  background: none;
  border: none;
  color: #dc2626;
  font-weight: 700;
  cursor: pointer;
  padding: 8px;
}

.btn-danger-text:hover {
  text-decoration: underline;
}

.filters-bar {
  margin-bottom: 16px;
}

.search-input {
  position: relative;
  max-width: 100%;
}

.search-input input {
  width: 100%;
  padding: 14px 14px 14px 48px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
  background: white;
}

.search-input input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.tabs-nav {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 32px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.tabs-nav::-webkit-scrollbar {
  display: none;
}

.tab-btn {
  background: none;
  border: none;
  padding: 12px 4px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
  white-space: nowrap;
}

.tab-btn:hover {
  color: #0f172a;
}

.tab-btn.active {
  color: #2563eb;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background: #2563eb;
  border-radius: 2px 2px 0 0;
}

.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
}

.property-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
  transition: transform 180ms ease, box-shadow 180ms ease;
  display: flex;
  flex-direction: column;
}

.property-card:hover {
  box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.1);
  border-color: #cbd5f5;
  transform: translateY(-6px);
}

.card-image-container {
  height: 200px;
  background: #f1f5f9;
  position: relative;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.property-tag {
  position: absolute;
  top: 16px;
  right: 16px;
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #0f172a;
}

.card-quick-actions {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s;
}

.property-card:hover .card-quick-actions {
  opacity: 1;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: white;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
}

.action-btn :deep(svg) {
  width: 16px;
  height: 16px;
}

.action-btn:hover {
  background: #2563eb;
  color: white;
}

.action-btn.delete-btn:hover {
  background: #ef4444;
}

.card-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-title {
  margin: 0 0 4px;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
}

.card-subtitle {
  margin: 0 0 16px;
  color: #64748b;
  font-size: 0.95rem;
}

.card-comuna {
  margin: -12px 0 16px;
  color: #94a3b8;
  font-size: 0.85rem;
}

.requests-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 16px;
}

.requests-chip.has-requests {
  color: #0f172a;
  background: #eef2ff;
  border-color: #c7d2fe;
}

.requests-count {
  font-weight: 700;
}

.btn-manage {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: none;
  background: #eff6ff;
  color: #2563eb;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-manage :deep(svg) {
  width: 16px;
  height: 16px;
}

.btn-manage:hover {
  background: #dbeafe;
}

.new-unit-card {
  border: 2px dashed #e2e8f0;
  border-radius: 20px;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
  transition: all 0.2s;
  min-height: 400px;
}

.new-unit-card:hover {
  border-color: #cbd5f5;
  background: #f1f5f9;
}

.new-unit-content h3 {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  margin: 16px 0 8px;
}

.new-unit-content p {
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #dbeafe;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  position: relative;
}

.icon-circle :deep(svg) {
  width: 32px;
  height: 32px;
}

.plus-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 24px;
  height: 24px;
  background: #2563eb;
  color: white;
  border-radius: 50%;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  isolation: isolate;
}

.modal-backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 0;
}

.modal-container {
  background-color: #ffffff !important;
  position: relative;
  border-radius: 20px;
  width: 100%;
  max-width: 650px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  animation: modalPop 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  opacity: 1 !important;
  isolation: isolate;
  z-index: 1;
}

@keyframes modalPop {
  from { transform: scale(0.96); }
  to { transform: scale(1); }
}

.modal-header {
  padding: 24px 28px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
}

.close-modal-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: color 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-modal-btn :deep(svg) {
  width: 18px;
  height: 18px;
}

.close-modal-btn:hover {
  color: #0f172a;
  background: #f1f5f9;
}

.modal-body {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.modal-footer-right {
  display: flex;
  gap: 12px;
}

.form-grid {
  display: grid;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field__label {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.95rem;
}

input, select {
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 1rem;
  outline: none;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
}

input:focus, select:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #94a3b8;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 8px 0;
}

.form-divider::before, .form-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.field__error {
  background: #fef2f2;
  color: #b91c1c;
  padding: 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin: 0;
}

.field__error :deep(svg) {
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .column-right {
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 70px;
  }

  .field-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .modal-container {
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .modal-footer {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .modal-footer-right {
    width: 100%;
  }

  .modal-footer-right button {
    flex: 1;
    justify-content: center;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #64748b;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
